"use client";

import { useEffect, useRef, useState } from "react";
import type { HfMapPoint } from "../model";

/**
 * "Where it is": MapLibre GL with OpenFreeMap vector tiles (no key; commercial use allowed; attribution to OpenMapTiles and
 * OpenStreetMap is required and shown under the map). The style is recoloured to Houseful's ground, the pin uses the brand
 * accent. MapLibre loads only in the browser, from a same-origin worker, so the CSP needs no `blob:` workers.
 * The storefront renders this only when map tiles are allowed by its CSP and the property has coordinates.
 */
type Palette = { ground: string; water: string; park: string; building: string; road: string; roadMajor: string; casing: string; label: string; halo: string };
const LIGHT: Palette = { ground: "#f3eee7", water: "#cfdce0", park: "#dce5d3", building: "#e8e1d7", road: "#ffffff", roadMajor: "#fbf7f1", casing: "#e9e2d8", label: "#6a635b", halo: "#f3eee7" };
const DARK: Palette = { ground: "#1f1a16", water: "#22313a", park: "#23291f", building: "#2a241f", road: "#3a332c", roadMajor: "#463e35", casing: "#2a241f", label: "#b3aa9f", halo: "#1f1a16" };
const HIDE = /shield|airport|aeroway|boundary|label_country|label_state|railway|ice_shelf|glacier|water_name_point|waterway_line_label|highway-name-path|road_pier|pier|poi|label_other|label_village|housenumber/;

type StyleLayer = { id: string; type: string };
type MapLike = {
  getStyle(): { layers: StyleLayer[] };
  setLayoutProperty(id: string, name: string, value: unknown): void;
  setPaintProperty(id: string, name: string, value: unknown): void;
};

function paint(map: MapLike, palette: Palette) {
  for (const layer of map.getStyle().layers) {
    const id = layer.id;
    try {
      if (HIDE.test(id)) map.setLayoutProperty(id, "visibility", "none");
      else if (layer.type === "background") map.setPaintProperty(id, "background-color", palette.ground);
      else if (layer.type === "fill") {
        const color = /water/.test(id) ? palette.water : /park|wood|grass/.test(id) ? palette.park : /building/.test(id) ? palette.building : palette.ground;
        map.setPaintProperty(id, "fill-color", color);
        if (/building/.test(id)) map.setPaintProperty(id, "fill-outline-color", palette.building);
      } else if (layer.type === "line") {
        const color = /waterway/.test(id) ? palette.water : /casing/.test(id) ? palette.casing : /motorway|major|trunk|primary/.test(id) ? palette.roadMajor : palette.road;
        map.setPaintProperty(id, "line-color", color);
      } else if (layer.type === "symbol") {
        map.setPaintProperty(id, "text-color", palette.label);
        map.setPaintProperty(id, "text-halo-color", palette.halo);
        map.setPaintProperty(id, "text-halo-width", 1.4);
      }
    } catch {
      // A layer without that property.
    }
  }
}

export function PlaceMap({ point, styleUrl, address }: { point: HfMapPoint; styleUrl: string; address: string | null }) {
  const box = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const container = box.current;
    if (!container) return;
    let disposed = false;
    let map: { remove(): void } | null = null;
    const scheme = container.closest<HTMLElement>(".hf");
    const dark = scheme ? getComputedStyle(scheme).getPropertyValue("--hf-scheme").trim() === "dark" : false;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    void import("maplibre-gl")
      .then(({ Map, Marker, setWorkerUrl }) => {
        if (disposed) return;
        setWorkerUrl(new URL("maplibre-gl/dist/maplibre-gl-worker.mjs", import.meta.url).toString());
        const instance = new Map({
          container,
          style: styleUrl,
          center: [point.lng, point.lat],
          zoom: 14.6,
          attributionControl: false,
          cooperativeGestures: true,
          dragRotate: false,
          pitchWithRotate: false,
          fadeDuration: reduce ? 0 : 300,
        });
        map = instance;
        instance.on("load", () => {
          paint(instance as unknown as MapLike, dark ? DARK : LIGHT);
          const pin = document.createElement("div");
          pin.className = "hf-pin";
          const dot = document.createElement("span");
          dot.className = "hf-pin-dot";
          const label = document.createElement("span");
          label.className = "hf-pin-label";
          label.textContent = point.label;
          pin.append(dot, label);
          new Marker({ element: pin, anchor: "left", offset: [-9, 0] }).setLngLat([point.lng, point.lat]).addTo(instance);
          container.dataset.ready = "";
        });
        instance.on("error", () => {
          if (!container.dataset.ready) setFailed(true);
        });
      })
      .catch(() => setFailed(true));
    return () => {
      disposed = true;
      map?.remove();
    };
  }, [point.lat, point.lng, point.label, styleUrl]);

  return (
    <div className="hf-map-wrap">
      <div className="hf-map" ref={box} role="img" aria-label={`Map of ${point.label}${address ? `, ${address}` : ""}`} data-failed={failed ? "" : undefined} />
      <p className="hf-map-attr">
        <a href="https://openfreemap.org" target="_blank" rel="noopener">
          OpenFreeMap
        </a>{" "}
        ·{" "}
        <a href="https://www.openmaptiles.org/" target="_blank" rel="noopener">
          © OpenMapTiles
        </a>{" "}
        ·{" "}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">
          © OpenStreetMap contributors
        </a>
      </p>
    </div>
  );
}
